import * as React from 'react'
import { useRef, useEffect } from 'react'
import { NestedCSSProperties } from 'typestyle/lib/types'
import Icon from '../../../common/src/components/Icon'
import { Spacer } from '../../../common/src/components/core'
import styled from '../../../common/src/components/styled'
import { Colors, DarkColors } from '../../../common/src/constants'
import { appContext } from '../../../common/src/context'
import {
  getTimeAgo,
  sortArrayByDate,
  dateMDYTimeTzFmt,
  dateTimeFmtWithAt,
  isAfter,
  addDays,
} from '../../../common/src/date'
import { formatPhoneNumber, listThingsInEnglish } from '../../../common/src/format'
import {
  FetchTranscriptQuery,
  FetchTranscriptQueryVariables,
  MessageType,
  ConfirmationStatus,
} from '../../../common/src/types.gen'
import { createRequestScopedClient } from '../apolloClientForLambda'
import { Space } from '../emails/components'
import { createWidget } from '../widgets/base'
import { bodyToEscapedHtml, getExternalPmUrl } from './components'
import { fetchTranscriptQuery } from './transcript-data'

const ILS_IGNORE_DAYS = 90
interface Props {
  transcript: NonNullable<FetchTranscriptQuery['transcript']>
  messageId?: number
}

const transcriptWidgetId = 'transcript-page'

const transcriptWidget = createWidget(transcriptWidgetId, (props: Props) => <Transcript {...props} />)

/**
 * Server-side function for rendering transcript HTML.
 *
 * Makes GraphQL API requests to appserver API to get transcript data for the provided UUID.
 */
export async function getTranscriptHtml(uuid: string, messageId?: number) {
  const client = createRequestScopedClient()
  const { data, error } = await client.query<FetchTranscriptQuery, FetchTranscriptQueryVariables>({
    query: fetchTranscriptQuery,
    variables: {
      threadUUID: uuid,
    },
  })

  if (error) {
    return error.message
  }

  if (data.transcript == null) {
    return 'transcript not found'
  }

  return getTranscriptHtmlForProps({ transcript: data.transcript, messageId })
}

export function getTranscriptHtmlForProps(props: Props) {
  const pageTitle = 'Transcript: ' + getDisplayName(props.transcript.thread.contactThem, true)
  return transcriptWidget.renderHtml(pageTitle, props, props)
}

function Transcript(props: Props) {
  const { transcript, messageId } = props
  const pmItemKey = transcript.thread.pmItemKeyJsons.find(k => getExternalPmUrl(k))
  return (
    <TranscriptContainer>
      {/* first, title the page, like: "Jasmine Fuller (901) 628-5160" */}
      {/* link to the guest card, if available */}
      <TranscriptHeader
        contact={transcript.thread.contactThem}
        address={transcript.thread.addressThem}
        pmItemKey={pmItemKey}
      />
      <TranscriptBody>
        <RelatedThreads transcript={transcript} />
        {/* load all Messages and showing history on this Contact's Thread */}
        <ChatContainer>{getTimeSortedElements(transcript, messageId)}</ChatContainer>
      </TranscriptBody>
      <Footer>
        <Space />
      </Footer>
    </TranscriptContainer>
  )
}

function TranscriptHeader(props: {
  contact: NonNullable<FetchTranscriptQuery['transcript']>['thread']['contactThem']
  address: string
  pmItemKey?: string
}) {
  return (
    <Header>
      <H1>{getDisplayName(props.contact, false)}</H1>
      {hasName(props.contact) && <HeaderSpan>{formatPhoneNumber(props.address)}</HeaderSpan>}
      {props.pmItemKey && <GuestCardLink pmItemKey={props.pmItemKey} />}
    </Header>
  )
}

function GuestCardLink(props: { pmItemKey: string }) {
  const link = getExternalPmUrl(props.pmItemKey)
  return link == null ? (
    <></>
  ) : (
    <div>
      <GuestCardLinkSpan>
        <A href={link}>show guest card »</A>
      </GuestCardLinkSpan>
    </div>
  )
}

function RelatedThreads(props: { transcript: NonNullable<FetchTranscriptQuery['transcript']> }) {
  const { transcript } = props
  const countRelatedThreads = transcript.thread.relatedThreads.length
  return countRelatedThreads <= 1 ? (
    <></>
  ) : (
    <>
      {getDisplayName(transcript.thread.contactThem, true)} has {countRelatedThreads} conversation threads with Lisa.
      Choose one below:
      <RelatedThreadContainer>
        {transcript.thread.relatedThreads.map((t, i) => (
          <RelatedThread key={i} thread={t} isSelected={transcript.thread.id === t.id} />
        ))}
      </RelatedThreadContainer>
    </>
  )
}

function RelatedThread(props: {
  thread: NonNullable<FetchTranscriptQuery['transcript']>['thread']['relatedThreads'][0]
  isSelected: boolean
}) {
  const { thread, isSelected } = props

  const iconName = thread.messageType === MessageType.EMAIL ? 'channel-email' : 'channel-sms'
  const timeAgo = getTimeAgo(thread.lastInboundMessageTime, new Date())
  const relativeUrl = '/api/transcript?uuid=' + thread.uuid

  const ThreadLink = isSelected ? RelatedThreadLinkSelected : RelatedThreadLink
  return (
    <ThreadLink href={relativeUrl}>
      <MessageTypeIcon>
        <Icon name={iconName} color={'silver'} />
      </MessageTypeIcon>
      <TaskName>{thread.taskName}</TaskName>
      <TimeAgo>{timeAgo}</TimeAgo>
    </ThreadLink>
  )
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function getTimeSortedElements(transcript: NonNullable<FetchTranscriptQuery['transcript']>, messageId?: number) {
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    setTimeout(() => scrollRef.current?.scrollIntoView({ block: 'center' }), 100)
  }, [scrollRef])

  const transcriptLines: Map<Date, JSX.Element[]> = new Map<Date, JSX.Element[]>()

  for (const m of transcript.messages) {
    const scrollRefToPass = m.id === messageId ? scrollRef : undefined
    const timeCreated = new Date(m.timeCreated)
    switch (m.messageType) {
      // for each Message.messageType, get the matching HTML element
      case MessageType.VOICE:
        if (m.isInbound && transcript.thread != null) {
          transcriptLines.set(
            timeCreated,
            getTranscriptVoiceHtml(m, transcript.thread.contactThem, transcript.timezone, scrollRefToPass)
          )
        }
        break
      case MessageType.SMS:
      case MessageType.EMAIL:
        const rolledUpILSMsgs = getRolledUpILSMsgs(transcript.messages, m)
        const sources: string[] = rolledUpILSMsgs
          .map(ilsMsg => ilsMsg.leasingAttributionSourceDisplayName)
          .filter((x: string | null | undefined): x is string => x !== null)
        if (transcript.thread) {
          transcriptLines.set(timeCreated, [
            getTranscriptSmsOrEmailHtml(
              m,
              transcript.thread.contactThem,
              transcript.timezone,
              sources,
              transcript.hidePhotoId,
              scrollRefToPass
            ),
          ])
        }
        break
      case MessageType.CALENDAR:
        if (m.isInbound) {
          throw new Error('Calendar message type cannot be inbound.')
        }
        if (!m.isInbound) {
          transcriptLines.set(timeCreated, [getTranscriptCalInviteHtml(m, transcript.timezone, scrollRefToPass)])
        }
        break
      default:
        break
    }
  }

  // To that sorted list of HTML elements, add all showing history status changes
  const showingIdToShowing: Map<number, NonNullable<FetchTranscriptQuery['transcript']>['showings'][0]> = new Map<
    number,
    NonNullable<FetchTranscriptQuery['transcript']>['showings'][0]
  >()
  transcript.showings.forEach(s => showingIdToShowing.set(s.id, s))
  for (const sc of transcript.showingContactHistories) {
    const show = showingIdToShowing.get(sc.showingId)
    if (show) {
      transcriptLines.set(
        new Date(sc.timeUpdated),
        getTranscriptUpdateHtml(sc.confirmationStatus, new Date(show.timeStart), transcript.timezone)
      )
    }
  }

  let res: any[] = []
  const keys: Date[] = []
  transcriptLines.forEach((value: JSX.Element[], key: Date) => keys.push(key))
  const sortedKeys = sortArrayByDate(keys)
  sortedKeys.forEach(k => (res = res.concat(transcriptLines.get(k))))
  return res
}

/**
 * Returns a list of Activities for a voice call message
 */
function getTranscriptVoiceHtml(
  message: NonNullable<FetchTranscriptQuery['transcript']>['messages'][0],
  them: NonNullable<FetchTranscriptQuery['transcript']>['thread']['contactThem'],
  tz: string,
  scrollRef?: React.RefObject<HTMLDivElement>
) {
  const endpointName = message.leasingAttributionSource
  const endpointNumber = message.isInbound ? message.addressTo : message.addressFrom
  const routedTo = message.routedTo
  const routedToStr = routedTo == null ? 'office phone' : formatPhoneNumber(routedTo)
  const endPointNameFragment = <span> via {endpointName}</span>
  const ActivityContainer = scrollRef ? ActivityHighlighted : Activity

  // If there are at least two (2) inbound calls already, add an extra HTML element to explain call forwarding
  const res = []
  res.push(
    <ActivityContainer>
      <div ref={scrollRef} />
      <TimeThem>{dateMDYTimeTzFmt(message.timeCreated, tz)}</TimeThem>
      <ActivityTitle>{getDisplayName(them, true)} calls in …</ActivityTitle>
      <ActivityMeta>
        <span>to {formatPhoneNumber(endpointNumber)}</span>
        {endpointName != null && endPointNameFragment}
      </ActivityMeta>
    </ActivityContainer>
  )

  if (routedTo != null) {
    res.push(
      <ActivityUs>
        <TimeThem>{dateMDYTimeTzFmt(message.timeCreated, tz)}</TimeThem>
        <ActivityTitle>Lisa forwards call to leasing office …</ActivityTitle>
        <ActivityMeta>
          <span>forwarding to {routedToStr}</span>
        </ActivityMeta>
      </ActivityUs>
    )
  }
  return res
}

/**
 * Returns SMS or EMAIL message as HTML element.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function getTranscriptSmsOrEmailHtml(
  m: NonNullable<FetchTranscriptQuery['transcript']>['messages'][0],
  them: NonNullable<FetchTranscriptQuery['transcript']>['thread']['contactThem'],
  tz: string,
  sources: string[],
  hidePhotoId: boolean,
  scrollRef?: React.RefObject<HTMLDivElement>
) {
  let body = bodyToEscapedHtml(m.censoredShortBody.substring(0, 1000))
  if (body === '' && !m.mediaUrl) {
    body = '(empty message)'
  }
  if (m.isIlsInbound) {
    const ActivityContainer = scrollRef ? ActivityHighlighted : Activity
    // render as widget-click
    return (
      <ActivityContainer>
        <div ref={scrollRef} />
        <TimeThem>{dateMDYTimeTzFmt(m.timeCreated, tz)}</TimeThem>
        <ActivityTitle>
          {getDisplayName(them, false)} clicks widget on {listThingsInEnglish(sources)}
        </ActivityTitle>
        <ActivityThemP>{body}</ActivityThemP>
      </ActivityContainer>
    )
  } else {
    const TimeComponent = m.isInbound ? TimeThem : TimeUs
    const mediaUrl = m.mediaUrl
    let img
    // do not show photo ID if the firm has hidePhotoId==true
    if (mediaUrl) {
      img = getImage(mediaUrl, m.classification, hidePhotoId)
    }

    return (
      <Container isInbound={m.isInbound} highlight={scrollRef != null}>
        <div ref={scrollRef} />
        <TimeComponent>{dateMDYTimeTzFmt(m.timeCreated, tz)}</TimeComponent>
        {getRenderedMessageBody(m, body, img, mediaUrl)}
      </Container>
    )
  }
}

type ContainerProps = React.PropsWithChildren<{
  isInbound: boolean
  highlight: boolean
}>

const Container = (props: ContainerProps) => {
  const { isInbound, highlight, children } = props

  if (highlight) {
    return isInbound ? (
      <ContainerThemHighlighted>{children}</ContainerThemHighlighted>
    ) : (
      <ContainerUsHighlighted>{children}</ContainerUsHighlighted>
    )
  }

  return isInbound ? <ContainerThem>{children}</ContainerThem> : <ContainerUs>{children}</ContainerUs>
}

function getRenderedMessageBody(
  m: NonNullable<FetchTranscriptQuery['transcript']>['messages'][0],
  body: any,
  img: any,
  mediaUrl?: string | null
) {
  const translation =
    m.censoredTranslatedShortBody && bodyToEscapedHtml(m.censoredTranslatedShortBody.substring(0, 1000))
  const shouldSwapBody = !m.isInbound && m.censoredTranslatedShortBody

  return (
    <MessageBody
      primaryBody={shouldSwapBody ? translation : body}
      secondaryBody={shouldSwapBody ? body : translation}
      mediaUrl={mediaUrl}
      img={img}
      isInbound={m.isInbound}
    />
  )
}

export function MessageBody({
  primaryBody,
  secondaryBody,
  mediaUrl,
  img,
  isInbound,
}: {
  primaryBody: any
  secondaryBody: any
  mediaUrl?: string | null
  img: any
  isInbound: boolean
}) {
  const TranslationComponent = isInbound ? TranslationThem : TranslationUs
  return (
    <>
      {primaryBody}
      {mediaUrl && img}
      {secondaryBody && (
        <TranslationComponent>
          <Spacer height={16} />
          {secondaryBody}
        </TranslationComponent>
      )}
    </>
  )
}

/**
 * If the image is a tagged photo ID and we're not allowed to display it, returns "<photo ID>". Otherwise returns an Img element.
 */
function getImage(url: string, msgClass: string | null | undefined, hide: boolean) {
  if (hide && msgClass === 'RECEIVE_PHOTO_ID') {
    return '<photo ID>'
  }
  return <Img src={url} alt="[Image From Prospect]" />
}
/**
 * Returns showing status update as HTML element (e.g. "OFFERED for appointment today at 9:30am")
 */
function getTranscriptUpdateHtml(status: ConfirmationStatus, timeStart: Date, tz: string) {
  return [
    <DivUpdate key={timeStart.toString()}>
      {status} for appointment {dateTimeFmtWithAt(timeStart, tz)}
    </DivUpdate>,
  ]
}

function getRolledUpILSMsgs(
  messages: NonNullable<FetchTranscriptQuery['transcript']>['messages'],
  fromMsg: NonNullable<FetchTranscriptQuery['transcript']>['messages'][0]
) {
  return messages.filter(
    m =>
      m.isIlsInbound &&
      !isAfter(fromMsg.timeCreated, m.timeCreated) &&
      isAfter(addDays(new Date(fromMsg.timeCreated), ILS_IGNORE_DAYS), m.timeCreated)
  )
}

/**
 * Returns calendar invite as HTML element.
 */
function getTranscriptCalInviteHtml(
  m: NonNullable<FetchTranscriptQuery['transcript']>['messages'][0],
  tz: string,
  scrollRef?: React.RefObject<HTMLDivElement>
) {
  const ActivityUsContainer = scrollRef ? ActivityUsHighlighted : ActivityUs
  return (
    <ActivityUsContainer>
      <div ref={scrollRef} />
      <TimeUs>{dateMDYTimeTzFmt(m.timeCreated, tz)}</TimeUs>
      <ActivityTitle>Lisa sends calendar invite</ActivityTitle>
      <ActivityUsP>{bodyToEscapedHtml(m.censoredShortBody.substring(0, 1000))}</ActivityUsP>
    </ActivityUsContainer>
  )
}

/**
 * Returns first name + last name or first name + last initial based on the value of shorten.
 */
function getDisplayName(
  contact: NonNullable<FetchTranscriptQuery['transcript']>['thread']['contactThem'],
  shorten: boolean
) {
  const fullName = [contact.firstName, contact.lastName].join(' ')
  if (fullName.replace(' ', '').length > 0) {
    return shorten ? getShortName(contact) : fullName
  } else if (contact.addressPhone != null) {
    return formatPhoneNumber(contact.addressPhone)
  } else {
    return contact.addressEmail
  }
}

/**
 * Returns true if contact has at least either first or last name
 */
function hasName(contact: NonNullable<FetchTranscriptQuery['transcript']>['thread']['contactThem']): boolean {
  return contact.firstName != null || contact.lastName != null
}

/**
 * Returns first name with last initial of Contact
 */
function getShortName(contact: NonNullable<FetchTranscriptQuery['transcript']>['thread']['contactThem']) {
  const lName = contact.lastName == null ? contact.lastName : (contact.lastName.substring(0, 1) + '.').toUpperCase()
  return [contact.firstName, lName].join(' ')
}

const font: NestedCSSProperties = {
  fontFamily:
    "'SF Mono', SFMono-Regular, 'Monaco', 'Roboto Mono', 'Inconsolata', 'Fira Mono', 'Droid Sans Mono', 'Source Code Pro', monospace",
}

const inlineBlock: NestedCSSProperties = {
  display: 'inline-block',
}

const TranscriptContainer = styled.blockquote({
  margin: '0 auto',
  width: '80%',
  maxWidth: '600px',
  wordBreak: 'break-word',
})

const TranscriptBody = styled.span({
  fontSize: '16px',
  fontWeight: 'normal',
  fontStretch: 'normal',
  fontStyle: 'bold',
  lineHeight: '1.5',
  textAlign: 'left',
  color: Colors.silver,
  marginBottom: '0px',
  paddingTop: '1em',
  display: 'block',
})

const timestampStyle: NestedCSSProperties = {
  display: 'block',
  fontSize: '10px',
  position: 'relative',
  top: '-0.4em',
  margin: '0',
  padding: '0',
}

/**
 * Translucent at first, so it doesn't take up much attention. If the user hovers, show at full opacity.
 */
const fadedToOpaqueOnHover: NestedCSSProperties = {
  transition: 'all 0.2s ease-in-out',
  opacity: 0.5,
  $nest: {
    '&:hover': {
      opacity: 1,
    },
  },
}

const TimeThem = styled.time(timestampStyle, fadedToOpaqueOnHover, {
  color: Colors.silver,
})

const TimeUs = styled.time(timestampStyle, fadedToOpaqueOnHover, {
  color: Colors.midway,
})

/**
 * Make sure translated text is small and has gray links
 */
const translatedLinkStyles: NestedCSSProperties = {
  fontSize: '12px',
  $nest: {
    A: {
      color: Colors.midway,
    },
  },
}
const TranslationThem = styled.div(fadedToOpaqueOnHover, translatedLinkStyles, {
  color: Colors.silver,
})

const TranslationUs = styled.div(fadedToOpaqueOnHover, translatedLinkStyles, {
  color: Colors.midway,
})

const Img = styled.img({
  maxWidth: '100%',
})

const margin = '0 0 1.3em 0'
const padding = '0.5em 1em 0.7em 1em'

const paragraphStyles: NestedCSSProperties = {
  margin,
  lineHeight: '1.3em',
  borderRadius: '1em',
  padding,
  background: 'white',
  maxWidth: '70%',
  clear: 'both',
  position: 'relative',
}

const ContainerThem = styled.div(paragraphStyles, {
  float: 'left',
  color: Colors.charcoal,
  $nest: {
    '&::after': {
      content: "''",
      position: 'absolute',
      left: '-0.5em',
      bottom: '0',
      width: '0.5em',
      height: '1em',
      borderRight: '0.5em solid white',
      borderBottomRightRadius: '1em 0.5em',
    },
  },
})

const ContainerThemHighlighted = styled.div(paragraphStyles, {
  float: 'left',
  color: Colors.charcoal,
  border: `2px solid ${Colors.sky}`,
})

const ContainerUs = styled.div(paragraphStyles, {
  float: 'right',
  backgroundColor: '#cecdcf',
  color: Colors.charcoal,
  $nest: {
    '&::after': {
      content: "''",
      position: 'absolute',
      right: '-0.5em',
      bottom: '0',
      width: '0.5em',
      height: '1em',
      borderLeft: '0.5em solid #cecdcf',
      borderBottomLeftRadius: '1em 0.5em',
    },
  },
})

const ContainerUsHighlighted = styled.div(paragraphStyles, {
  float: 'right',
  backgroundColor: '#cecdcf',
  color: Colors.charcoal,
  border: `2px solid ${Colors.sky}`,
})

const A = styled.a({
  color: DarkColors.sky,
  transition: 'all 0.1s ease-in-out',
  $nest: {
    '&:hover': {
      filter: 'brightness(1.3)',
    },
    '&:focus': {
      outline: 'none',
      filter: 'brightness(1.3)',
    },
    '&:active': {
      outline: 'none',
      filter: 'brightness(1.5)',
    },
  },
})

const Header = styled.header({
  margin: '4em 0 0 0',
})

const H1 = styled.h1(inlineBlock, {
  color: Colors.charcoal,
  fontSize: '32px',
  fontWeight: 'normal',
  fontStyle: 'normal',
  fontStretch: 'normal',
  lineHeight: '1.5',
  margin: '0',
})

const HeaderSpan = styled.span(font, inlineBlock, {
  color: Colors.midway,
  margin: '0.5em',
})

const activityStyles: NestedCSSProperties = {
  backgroundColor: 'rgba(16, 16, 17, 0.05)',
  color: '#3d3c40',
  borderRadius: '0.5em',
  display: 'block',
  float: 'left',
  clear: 'both',
  padding: '1em',
  marginBottom: '1.3em',
  maxWidth: '70%',
}

const Activity = styled.div(activityStyles)

const ActivityHighlighted = styled.div(activityStyles, {
  border: `2px solid ${Colors.sky}`,
})

const ActivityTitle = styled.span({
  padding: '0.3em 0 0.6em 0',
  display: 'block',
})

const ActivityMeta = styled.div({
  fontSize: '0.75em',
  color: 'rgba(0, 0, 0, 0.3)',
  display: 'block',
})

const ActivityThemP = styled.p({
  margin,
  lineHeight: '1.3em',
  borderRadius: '1em',
  padding,
  background: 'white',
  clear: 'both',
  position: 'relative',
  top: '1.3em',
  maxWidth: '100%',
  fontSize: '0.7em',
  color: Colors.silver,
  float: 'left',
  $nest: {
    '&::after': {
      content: "''",
      position: 'absolute',
      left: '-0.5em',
      bottom: '0',
      width: '0.5em',
      height: '1em',
      borderRight: '0.5em solid white',
      borderBottomRightRadius: '1em 0.5em',
    },
  },
})

const activityUsStyles: NestedCSSProperties = {
  backgroundColor: 'rgba(16, 16, 17, 0.05)',
  color: Colors.steel,
  borderRadius: '0.5em',
  display: 'block',
  clear: 'both',
  padding: '1em',
  marginBottom: '1.3em',
  float: 'right',
}

const ActivityUs = styled.div(activityUsStyles)

const ActivityUsHighlighted = styled.div(activityUsStyles, {
  border: `2px solid ${Colors.sky}`,
})

const ActivityUsP = styled.p({
  margin,
  lineHeight: '1.3em',
  borderRadius: '1em',
  padding: '0.5em 1em 0.7em 1em',
  background: 'white',
  clear: 'both',
  position: 'relative',
  float: 'right',
  backgroundColor: '#cecdcf',
  top: '1.3em',
  maxWidth: '100%',
  fontSize: '0.7em',
  color: Colors.midway,
})

const DivUpdate = styled.div(font, fadedToOpaqueOnHover, {
  clear: 'both',
  textAlign: 'center',
  maxWidth: '80%',
  width: '70%',
  color: Colors.silver,
  fontSize: '0.7em',
  margin: '2em auto',
})

const GuestCardLinkSpan = styled.span(font, inlineBlock, {
  position: 'absolute',
  margin: '0.5em',
  top: '0',
  right: '0',
  textAlign: 'right',
  zIndex: 10,
  height: '50px',
  minWidth: '100px',
  padding: '12px',
  color: Colors.midway,
})

const ChatContainer = styled.div({
  marginTop: '2em',
})

const RelatedThreadContainer = styled.div({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexWrap: 'wrap',
})

const relatedThreadStyles: NestedCSSProperties = {
  textDecoration: 'none',
  color: Colors.charcoal,
  maxWidth: '29%',
  width: '29%',
  padding: '16px',
  boxSizing: 'border-box',
  margin: '16px 16px 0 0',
  borderRadius: '0.25em',
  fontSize: '12px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  border: '1px solid #eee',
  transition: 'all 0.15s ease-in',
}

const RelatedThreadLink = styled.a(relatedThreadStyles, {
  opacity: 0.4,
  $nest: {
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      opacity: 1,
    },
  },
})

const RelatedThreadLinkSelected = styled.a(relatedThreadStyles, {
  backgroundColor: 'white',
  $nest: {
    '&:hover': {
      backgroundColor: 'white',
      borderColor: '#dddddd',
    },
  },
})

const MessageTypeIcon = styled.span({
  display: 'block',
  height: '12px',
  width: '12px',
  paddingBottom: '12px',
})

const TaskName = styled.span({
  textDecoration: 'none',
  display: 'block',
  marginBottom: '4px',
})

const TimeAgo = styled.span({
  color: Colors.silver,
  display: 'block',
})

const Footer = styled.footer({
  minHeight: '10em',
  display: 'block',
  clear: 'both',
})

export function hydrateTranscriptWidget() {
  // Only hydrate widget if it's actually rendered
  if (document.getElementById(transcriptWidgetId) == null) {
    return
  }

  transcriptWidget.init(appContext().hydrateProps)
}
